import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BusinessIcon from '@mui/icons-material/Business';
import CalculateIcon from '@mui/icons-material/Calculate';
import CallIcon from '@mui/icons-material/Call';
import DescriptionIcon from '@mui/icons-material/Description';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import EditNoteIcon from '@mui/icons-material/EditNote';
import EventNoteIcon from '@mui/icons-material/EventNote';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import { hasPermission } from '@relif/zeller-roles';
import type React from 'react';
import type { BusinessUnit } from 'services/types/businessUnit';
import type { Client } from 'services/types/client';
import type { Lead } from 'services/types/lead';
import type { User } from 'services/types/user';
import { FINANCIAL_INSTITUTION_BUSINESS_UNIT_IDS, INSUREX_BUSINESS_UNIT_ID } from 'utils/constants';

import {
  renderAdminSettings,
  renderBankOffers,
  renderCallManager,
  renderCarDealerInfo,
  renderClientCars,
  renderClientNotes,
  renderDocuments,
  renderLeads,
  renderOperationalInfo,
  renderPersonalInfo,
  renderSales,
  renderScoring,
  renderSimulator,
  renderSurveys,
  renderUserAssignment,
  renderVehicleAndQuotes,
  renderVisitInfo,
} from './sectionRenderers';

// Business Unit Types
type BusinessUnitType = 'car_dealership' | 'financial_institution' | 'insurance_broker' | 'after_sales' | 'default';

// Section Configuration
export interface SectionConfig {
  id: string;
  title: string;
  icon: React.ReactElement;
  defaultExpanded?: boolean;
  /** When true, content is mounted only the first time the section is expanded. */
  lazy?: boolean;
  showForBusinessUnits: BusinessUnitType[];
  showCondition?: (
    client: Client,
    currentUser?: User | null,
    clientLeads?: Lead[],
    businessUnit?: BusinessUnit,
    isConversationActive?: boolean
  ) => boolean;
  renderContent: (
    client: Client,
    businessUnit?: BusinessUnit,
    currentUser?: User | null,
    clientLeads?: Lead[],
    isConversationActive?: boolean
  ) => React.ReactNode;
}

/**
 * Configuration for accordion sections shown below the identity hero card and notes card.
 *
 * Identity (name, contact, tags) is rendered by `ClientIdentityHero` and notes
 * for sellers by `ClientNotesCard` — both mounted directly in `ClientInfoPanel`.
 *
 * Section order (executive-first):
 * 1. Business unit info (car / insurance / financial)
 * 2. Documents
 * 3. Leads
 * 4. Calls
 * 5. Personal info (nationality, address, bank)
 * 6. Scoring (admin)
 * 7. Surveys (conditional)
 * 8. Notes (admin only)
 * 9. Operational info
 * 10. User assignment (conditional)
 * 11. Admin settings (admin)
 */
export const getSectionConfigs = (): SectionConfig[] => [
  // 1. Business Unit Specific Information — most relevant context for the executive
  {
    id: 'car-dealer-info',
    title: 'Información Automotriz',
    icon: <BusinessIcon />,
    defaultExpanded: true,
    showForBusinessUnits: ['car_dealership'],
    renderContent: renderCarDealerInfo,
  },
  {
    id: 'visit-info',
    title: 'Información de visita',
    icon: <EventNoteIcon />,
    defaultExpanded: true,
    showForBusinessUnits: ['after_sales'],
    renderContent: renderVisitInfo,
  },
  {
    id: 'client-cars-info',
    title: 'Vehículos del cliente',
    icon: <DirectionsCarIcon />,
    defaultExpanded: true,
    showForBusinessUnits: ['after_sales'],
    renderContent: renderClientCars,
  },
  {
    id: 'simulator',
    title: 'Simulador',
    icon: <CalculateIcon color="primary" />,
    defaultExpanded: false,
    lazy: true,
    showForBusinessUnits: ['insurance_broker'],
    showCondition: (_client, _currentUser, _clientLeads, businessUnit) => businessUnit?.id === INSUREX_BUSINESS_UNIT_ID,
    renderContent: renderSimulator,
  },
  {
    id: 'sales-info',
    title: 'Información de Portabilidades',
    icon: <AssignmentIcon color="primary" />,
    defaultExpanded: true,
    showForBusinessUnits: ['insurance_broker'],
    showCondition: (_client, _currentUser, _clientLeads, businessUnit) => businessUnit?.id === INSUREX_BUSINESS_UNIT_ID,
    renderContent: renderSales,
  },
  {
    id: 'vehicle-quotes-info',
    title: 'Vehículo y cotizaciones',
    icon: <DirectionsCarIcon color="primary" />,
    defaultExpanded: false,
    showForBusinessUnits: ['insurance_broker'],
    showCondition: (_client, _currentUser, _clientLeads, businessUnit) => businessUnit?.id !== INSUREX_BUSINESS_UNIT_ID,
    renderContent: renderVehicleAndQuotes,
  },
  {
    id: 'bank-offers',
    title: 'Ofertas de Banco',
    icon: <AccountBalanceIcon />,
    defaultExpanded: true,
    lazy: true,
    showForBusinessUnits: ['car_dealership', 'financial_institution', 'insurance_broker', 'after_sales', 'default'],
    showCondition: (_client, _currentUser, _clientLeads, businessUnit) =>
      !!businessUnit && FINANCIAL_INSTITUTION_BUSINESS_UNIT_IDS.includes(businessUnit.id),
    renderContent: renderBankOffers,
  },
  // 2. Documents
  {
    id: 'documents-info',
    title: 'Documentos',
    icon: <DescriptionIcon />,
    defaultExpanded: false,
    lazy: true,
    showForBusinessUnits: ['car_dealership', 'financial_institution', 'insurance_broker', 'after_sales', 'default'],
    renderContent: renderDocuments,
  },
  // 3. Leads
  {
    id: 'leads-info',
    title: 'Leads',
    icon: <LeaderboardIcon />,
    defaultExpanded: false,
    showForBusinessUnits: ['car_dealership', 'financial_institution', 'insurance_broker', 'after_sales', 'default'],
    renderContent: renderLeads,
  },
  // 4. Call Manager — moved below leads; less prominent than docs/notes
  {
    id: 'call-manager',
    title: 'Llamadas',
    icon: <CallIcon />,
    defaultExpanded: false,
    lazy: true,
    showForBusinessUnits: ['car_dealership', 'financial_institution', 'insurance_broker', 'after_sales', 'default'],
    renderContent: renderCallManager,
    showCondition: (_client, _currentUser, _clientLeads, businessUnit) => !!businessUnit?.hasCallingEnabled,
  },
  // 5. Información Personal (nationality, address, bank)
  {
    id: 'personal-info',
    title: 'Información Personal',
    icon: <PersonIcon />,
    defaultExpanded: false,
    showForBusinessUnits: ['car_dealership', 'financial_institution', 'insurance_broker', 'after_sales', 'default'],
    renderContent: renderPersonalInfo,
  },
  // 6. Scoring — admins only
  {
    id: 'scoring',
    title: 'Scoring',
    icon: <AssessmentIcon />,
    defaultExpanded: false,
    showForBusinessUnits: ['car_dealership', 'financial_institution', 'insurance_broker', 'after_sales', 'default'],
    showCondition: (_client, currentUser) => !!currentUser?.isAdmin,
    renderContent: renderScoring,
  },
  // 7. Surveys (only when client has surveys)
  {
    id: 'surveys',
    title: 'Encuestas',
    icon: <DescriptionIcon />,
    defaultExpanded: false,
    showForBusinessUnits: ['car_dealership', 'financial_institution', 'insurance_broker', 'after_sales', 'default'],
    showCondition: (client) => !!(client.surveys && client.surveys.length > 0),
    renderContent: renderSurveys,
  },
  // 8. Notes — admin only (sellers see notes in the always-visible card above)
  {
    id: 'client-notes-admin',
    title: 'Notas del Cliente',
    icon: <EditNoteIcon />,
    defaultExpanded: false,
    showForBusinessUnits: ['car_dealership', 'financial_institution', 'insurance_broker', 'after_sales', 'default'],
    showCondition: (_client, currentUser) =>
      currentUser?.role !== 'seller' && currentUser?.role !== 'sellerWithAssignment',
    renderContent: renderClientNotes,
  },
  // 9. Operational Info
  {
    id: 'operational-info',
    title: 'Estado y Configuración',
    icon: <SettingsIcon />,
    defaultExpanded: false,
    showForBusinessUnits: ['car_dealership', 'financial_institution', 'insurance_broker', 'after_sales', 'default'],
    renderContent: renderOperationalInfo,
  },
  // 10. User Assignment (when applicable)
  {
    id: 'user-assignment',
    title: 'Asignación de Usuario',
    icon: <PersonIcon />,
    defaultExpanded: false,
    showForBusinessUnits: ['car_dealership', 'financial_institution', 'insurance_broker', 'after_sales', 'default'],
    showCondition: (client, currentUser, _clientLeads, businessUnit) => {
      return !!(
        businessUnit?.hasSellerManagement &&
        currentUser &&
        hasPermission(currentUser, 'clients', 'assign', client)
      );
    },
    renderContent: (client: Client) => renderUserAssignment(client),
  },
  // 11. Admin Settings (admins only)
  {
    id: 'admin-settings',
    title: 'Configuración Administrativa',
    icon: <SettingsIcon />,
    defaultExpanded: false,
    showForBusinessUnits: ['car_dealership', 'financial_institution', 'insurance_broker', 'after_sales', 'default'],
    showCondition: (_client, currentUser) => !!currentUser?.isAdmin,
    renderContent: renderAdminSettings,
  },
];
